import { BookingRemarks } from '@models/booking-remarks.model';
import { TranslateModule } from '@ngx-translate/core';
import { Popover, PopoverModule } from 'primeng/popover';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  untracked,
  viewChild
} from '@angular/core';
import { HoverCardCoordinator } from './hover-card-coordinator.service';
import { HoverCardState } from './hover-card-state';

/** Ennyi ideig marad nyitva a kártya, miután az egér elhagyta: elég, hogy a gombról átérjen a kártyára. */
const HOVER_CLOSE_GRACE_MS = 150;

/** Ilyen gyakran próbálja újra a megnyitást, amíg az előző zárás animációja fut (kb. egy képkocka). */
const SYNC_RETRY_MS = 16;

/** A kártya nyila a 20 px-es gomb közepére mutasson (a PrimeNG alapértéke 1.25rem). */
const CARD_STYLE = { '--p-popover-arrow-offset': '0.625rem' } as const;

/**
 * Jelzés a vendég neve mellett, ha a foglaláshoz a külső rendszerből megjegyzés érkezett.
 * Megjegyzés nélkül semmit nem renderel. A kártya egérrel fölé víve azonnal nyílik, billentyűzettel
 * fókuszra, érintőképernyőn koppintásra; kattintással rögzíthető, Esc-re és kívülre kattintva zár.
 */
@Component({
  selector: 'app-booking-remarks',
  standalone: true,
  imports: [TranslateModule, PopoverModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (remarks(); as current) {
      <button
        #trigger
        type="button"
        class="relative inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-sky-500 p-0 text-white shadow-sm transition-[transform,background-color,box-shadow] duration-150 ease-out before:absolute before:-inset-2 before:content-[''] hover:scale-110 hover:bg-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 active:scale-95 dark:bg-sky-400 dark:text-sky-950 dark:hover:bg-sky-300"
        [class.ring-2]="isPinned()"
        [class.ring-sky-300]="isPinned()"
        [attr.aria-label]="'bookingOverview.remarks.indicator' | translate"
        [attr.aria-expanded]="isOpen()"
        aria-haspopup="dialog"
        (pointerenter)="onPointerEnter($event)"
        (pointerleave)="onPointerLeave($event)"
        (focus)="onTriggerFocus()"
        (blur)="onFocusOut($event)"
        (click)="onTriggerClick($event)"
      >
        <i class="pi pi-info text-[0.625rem] font-bold" aria-hidden="true"></i>
      </button>

      <p-popover
        [focusOnShow]="false"
        [style]="cardStyle"
        styleClass="min-w-56 max-w-[min(24rem,calc(100vw-2rem))]"
        [ariaLabel]="'bookingOverview.remarks.indicator' | translate"
        (onHide)="onCardHidden()"
      >
        <dl
          #cardContent
          data-remarks-card
          class="m-0 flex max-h-[min(18rem,60vh)] flex-col overflow-y-auto overscroll-contain"
          (pointerenter)="onPointerEnter($event)"
          (pointerleave)="onPointerLeave($event)"
          (focusin)="onCardFocusIn()"
          (focusout)="onFocusOut($event)"
        >
          @if (current.customerRemarks; as text) {
            <div class="flex animate-fadein flex-col gap-1 animate-duration-200 animate-ease-out">
              <dt class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-color">
                <i class="pi pi-user text-[0.6875rem]" aria-hidden="true"></i>
                {{ 'bookingOverview.remarks.customerRemarks' | translate }}
              </dt>
              <dd class="m-0 whitespace-pre-line break-words text-sm leading-relaxed text-color">{{ text }}</dd>
            </div>
          }
          @if (current.comment; as text) {
            <div
              class="flex animate-fadein flex-col gap-1 border-surface animate-delay-[50ms] animate-duration-200 animate-ease-out animate-fill-both"
              [class.mt-3]="hasBoth()"
              [class.border-t]="hasBoth()"
              [class.pt-3]="hasBoth()"
            >
              <dt class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-color">
                <i class="pi pi-comment text-[0.6875rem]" aria-hidden="true"></i>
                {{ 'bookingOverview.remarks.comment' | translate }}
              </dt>
              <dd class="m-0 whitespace-pre-line break-words text-sm leading-relaxed text-color">{{ text }}</dd>
            </div>
          }
        </dl>
      </p-popover>
    }
  `
})
export class BookingRemarksComponent {
  readonly remarks = input<BookingRemarks | undefined>();

  private readonly state = new HoverCardState();
  private readonly coordinator = inject(HoverCardCoordinator);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  private readonly cardContent = viewChild<ElementRef<HTMLElement>>('cardContent');
  private readonly card = viewChild(Popover);
  private closeTimer?: ReturnType<typeof setTimeout>;
  private syncTimer?: ReturnType<typeof setTimeout>;
  private hideRequested = false;

  /** A szülő ezzel nyomhatja el a saját tooltipjét, amíg a kártya látszik. */
  readonly isOpen = this.state.isOpen;
  protected readonly isPinned = this.state.isPinned;
  protected readonly hasBoth = computed(() => !!this.remarks()?.customerRemarks && !!this.remarks()?.comment);
  protected readonly cardStyle = CARD_STYLE;

  constructor() {
    effect(() => {
      const open = this.state.isOpen();
      untracked(() => this.applyOpen(open));
    });
    effect(() => {
      if (!this.remarks()) {
        untracked(() => this.state.reset());
      }
    });
    inject(DestroyRef).onDestroy(() => this.dispose());
  }

  /** Csak valódi egér nyit hoverre; érintésnél a koppintás (click) dönt, különben nyitás után rögtön zárna. */
  protected onPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(this.closeTimer);
    this.state.add('hover');
  }

  protected onPointerLeave(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.state.remove('hover'), HOVER_CLOSE_GRACE_MS);
  }

  /** Csak billentyűzetes fókuszra nyit: egérkattintásnál a fókusz nem „látható", ott a click dönt. */
  protected onTriggerFocus(): void {
    if (this.trigger()?.nativeElement.matches(':focus-visible')) {
      this.state.add('focus');
    }
  }

  protected onCardFocusIn(): void {
    this.state.add('focus');
  }

  /**
   * A gomb és a kártya közötti fókuszváltás nem zár. Ha a fókusz máshová kerül (pl. Tab), bezár;
   * ha nincs új fókuszcél (pl. a kártya szövegére kattintottak), csak a fókusz-ok szűnik meg.
   */
  protected onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget;
    if (next instanceof Node && this.isInsideCardArea(next)) {
      return;
    }
    if (next) {
      this.state.reset();
      return;
    }
    this.state.remove('focus');
  }

  /** A kattintás ne jusson el a szülőig (a Távozások fülön a vendég-blokk a részletekre navigál). */
  protected onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    this.state.togglePin();
  }

  /** A kártya magától is bezárulhat (kívülre kattintás, Esc, görgetés, átméretezés): ilyenkor az állapot is nulláz. */
  protected onCardHidden(): void {
    if (!this.hideRequested) {
      this.state.reset();
    }
    this.hideRequested = false;
    this.syncSoon();
  }

  private applyOpen(open: boolean): void {
    if (open) {
      this.coordinator.activate(this.state);
      this.showCard();
      return;
    }
    this.coordinator.release(this.state);
    this.hideCard();
  }

  /**
   * A PrimeNG a még futó zárás végén nullázza a célelemet, és animáció közben a megnyitást eldobja;
   * ilyenkor nem nyitunk rá, hanem a zárás lefutása után újra egyeztetünk.
   */
  private showCard(): void {
    const card = this.card();
    const trigger = this.trigger()?.nativeElement;
    if (!card || !trigger || card.overlayVisible) {
      return;
    }
    if (card.render || card.isOverlayAnimationInProgress) {
      this.syncSoon();
      return;
    }
    card.show(null, trigger);
  }

  private syncSoon(): void {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.applyOpen(this.state.isOpen()), SYNC_RETRY_MS);
  }

  private hideCard(): void {
    const card = this.card();
    if (!card?.overlayVisible) {
      return;
    }
    this.hideRequested = true;
    card.hide();
  }

  private isInsideCardArea(node: Node): boolean {
    return node === this.trigger()?.nativeElement || !!this.cardContent()?.nativeElement.contains(node);
  }

  private dispose(): void {
    clearTimeout(this.closeTimer);
    clearTimeout(this.syncTimer);
    this.coordinator.release(this.state);
  }
}
